import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./ForgotPassword.module.css";
import logo from "../../assets/house-icon-red.png";
import bgImg from "../../assets/citystrip.png";
import { BASE_URL } from "../../App";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // msg state structure: { type: "error" | "success" | "", text: string }
  const [msg, setMsg] = useState({ type: "", text: "" }); 
  const [sendOk, setSendOk] = useState(false);
  const navigate = useNavigate();

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // useEffect to automatically clear error messages after 5 seconds
  useEffect(() => {
    // Only set a timer if there is an error message visible
    if (msg.text && msg.type === "error") {
      const timer = setTimeout(() => {
        setMsg({ type: "", text: "" }); // Clear the message
      }, 5000); // 5000 milliseconds = 5 seconds

      // Cleanup function to clear the timer if the component unmounts
      // or if the message state changes before 5s (e.g., user types)
      return () => clearTimeout(timer);
    }
  }, [msg]); // Re-run effect whenever the message state changes

  // Function to wrap setMsg and handle the clear-on-input logic
  const handleSetMsg = (newMsg) => {
      setMsg(newMsg);
      // If setting a new message, stop clearing timer if it was running for previous message
      // (The useEffect above handles the new timeout logic)
  }

  async function onSubmit(e) {
    e.preventDefault();
    setTouched(true);
    setSendOk(false);
    
    // Validate email client-side first
    if (!validEmail) {
      handleSetMsg({ type: "error", text: "Please enter a valid email address." });
      return;
    }

    try {
      setSubmitting(true);
      handleSetMsg({ type: "", text: "" }); // Clear previous message before submission

      const res = await fetch(`${BASE_URL}backend/forgot_password/request.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data?.ok) {
        handleSetMsg({ type: "success", text: data.message || "OTP has been sent to your email." });
        setSendOk(true);
      } else {
        // Clear message immediately if we set a new error, relying on useEffect to clear it after 5s
        handleSetMsg({ type: "error", text: data?.error || "Something went wrong. Please try again." });
        setSendOk(false);
      }
    } catch {
      handleSetMsg({ type: "error", text: "Something went wrong. Please check your network connection." });
      setSendOk(false);
    } finally {
      setSubmitting(false);
    }
  }

  // Clear message when email input changes, only if the current message is an error
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Clear error message instantly if user starts fixing the input
    if (msg.type === "error") {
      setMsg({ type: "", text: "" });
    }
  };


  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <img src={logo} alt="HomeMate logo" className={styles.headerLogo} />
          <span className={styles.headerBrand}>HomeMate</span>
        </div>
      </header>

      <section className={styles.hero}>
        <div className={styles.center}>
          <h1 className={styles.title}>
            Forgot Your <br />
            <span className={styles.titleEmph}>Password?</span>
          </h1>
          <p className={styles.subtitle}>
            Enter your email and we’ll send you a recovery code
          </p>

          <form onSubmit={onSubmit} noValidate className={styles.form}>
            <label htmlFor="email" className={styles.label}>Email*</label>
            <input
              id="email"
              type="email"
              placeholder="example@example.com"
              className={[
                styles.input,
                touched && !validEmail ? styles.inputError : "",
              ].join(" ")}
              value={email}
              onChange={handleEmailChange} // Use the new handler
              onBlur={() => setTouched(true)}
              aria-invalid={touched && !validEmail}
            />

            <button className={styles.primaryBtn} disabled={submitting}>
              {submitting ? "Sending..." : "Continue"}
            </button>
          </form>

          {/* Proceed button only after a successful send */}
          <div style={{ maxWidth: 480, margin: "16px auto 0" }}>
            <button
              className={styles.primaryBtn}
              style={{ background: "#1f2937" }}
              disabled={!sendOk}
              onClick={() => { window.location.hash = `#/reset-password?email=${encodeURIComponent(email)}`; }}
            >
              I have a code
            </button>
          </div>
        </div>

        {/* Message Display - This is where the error/success message appears */}
        {msg.text && (
          <div
            className={
              msg.type === "success" ? styles.alertSuccess : styles.alertError
            }
            role="status"
          >
            {msg.text}
          </div>
        )}

        <div
          className={styles.cityStrip}
          style={{ backgroundImage: `url(${bgImg})` }}
          aria-hidden
        />
      </section>

      <div className={styles.footerLinks}>
        <Link to="/login" className={styles.linkDark}>Back to Login</Link>
      </div>
    </div>
  );
}